import { VbzButton, VbzModal, VbzInputText, VbzCheckbox } from '@smiles/voebiz-ui-kit';
import React, { FC, useCallback, useState, useEffect, useMemo, useRef } from 'react';
import queryString from 'query-string';
import { ModalBody } from 'reactstrap';
import { useFlags } from 'launchdarkly-react-client-sdk';
import ReCAPTCHA from 'react-google-recaptcha';
import { cnpj } from 'cpf-cnpj-validator';
import { handleChangeDocumentCnpj } from '../../utils/documentUtils';
import useAuth from '../../hooks/useAuth';
import { MemberT } from '../../types/types';
import { memberService } from '../../services/memberService';

export interface ModalConfirmationProps {
  isOpenModalConfirmation: boolean;
  toggleModalConfirmation: () => void;
  onClickContinue: (cnpjValue: string) => Promise<void>;
  onClickRule: () => void;
  promoLink: string;
}

const ModalConfirmation: FC<ModalConfirmationProps> = ({
  isOpenModalConfirmation,
  toggleModalConfirmation,
  onClickContinue,
  onClickRule,
  promoLink,
}) => {
  const urlParams = useMemo(() => queryString.parse(window.location.search), []);
  const [cnpjDocument, setCnpjDocument] = useState('');
  const [validated, setValidated] = useState(false);
  const [isChecked, setIsChecked] = useState(false);
  const [recaptchaValue, setRecaptchaValue] = useState('');
  // const [isLoading, setIsLoading] = useState(false);

  const recaptchaComp = useRef<any>();

  const { isLogged } = useAuth();
  const ldFlags = useFlags();
  const { apiDomain } = ldFlags;

  const validateCnpjFormat = (value: string) => {
    const isValid = cnpj.isValid(value);
    setValidated(isValid);
    return isValid;
  };

  const handleCheckConfirmCheckbox = (): void => {
    setIsChecked(!isChecked);
  };

  const formatCNPJ = (value?: string) => {
    return value ? cnpj.format(value) : '';
  };

  useEffect(() => {
    if (isLogged) {
      memberService(apiDomain)
        .get()
        .then((userData: MemberT) => {
          setCnpjDocument(formatCNPJ(userData?.member.CNPJ));
        })
        .catch((e) => console.error(e));
    }
  }, [isLogged]);

  return (
    <>
      <VbzModal isOpen={isOpenModalConfirmation} type="right" desktopSize="small" toggle={toggleModalConfirmation}>
        <ModalBody>
          <div className="modal-confirmation__title">
            <h3>Confirme a sua participação!</h3>
          </div>

          <div className="modal-confirmation__description">
            <p>
              Wow! Essa promoção é a sua cara mesmo! Se tiver alguma dúvida, confira{' '}
              <a href={promoLink} target="_blank" rel="noopener noreferrer">
                como funciona
              </a>
            </p>
          </div>
          <div className="modal-confirmation__input">
            <div className="voebiz-cnpj_form_fadeIn-late">
              <VbzInputText
                id="inp_cnpj"
                data-testid="inp_cnpj"
                textLabel="Qual é o seu CNPJ?"
                placeholder="Ex.: 12.345.678/0001-23"
                onChange={(e) => handleChangeDocumentCnpj(e, setCnpjDocument, setRecaptchaValue)}
                value={cnpjDocument}
                maxLength={18}
                validate={validateCnpjFormat}
                errorMessage="O CNPJ informado não é válido. Por favor, tente de novo."
                inputMode="numeric"
                regex={/[^0-9./-]/gi}
                role="textbox"
                disabled={isLogged}
              />
            </div>

            <div className={`modal-confirmation__recaptcha-wrapper ${validated ? '' : 'disabled'}`}>
              {!isLogged ? (
                validated &&
                ldFlags.recaptchaSiteKey && (
                  <ReCAPTCHA
                    data-testid="recaptcha"
                    ref={recaptchaComp}
                    onExpired={() => {
                      setRecaptchaValue('');
                    }}
                    sitekey={ldFlags.recaptchaSiteKey}
                    onChange={(value) => {
                      setRecaptchaValue(value || '');
                    }}
                  />
                )
              ) : (
                <div />
              )}
            </div>
            <div className="modal-confirmation__checkbox-wrapper">
              <VbzCheckbox
                className="modal-confirmation__checkbox-wrapper__check"
                id="use_terms_agreement"
                data-testid="use_terms_agreement"
                checked={isChecked}
                onClick={handleCheckConfirmCheckbox}
              />
              <span id="use_terms_agreement_label">
                Li e concordo com os termos e condições do{' '}
                <div className="error-modal-rules-btn">
                  <VbzButton
                    id="modal-rules__button"
                    className="confirmation-modal-button"
                    color="hyperlink"
                    text="regulamento"
                    onClick={onClickRule}
                  />
                </div>
              </span>
            </div>
          </div>
          <div className="error-modal-btn animation">
            <VbzButton
              id="modal-confirmation__button"
              className="confirmation-modal-button"
              color="primary"
              text="Confirmar"
              // isLoading={isLoading}
              onClick={async () => {
                // setIsLoading(true);
                await onClickContinue(cnpj.strip(cnpjDocument));
                setRecaptchaValue('');
                toggleModalConfirmation();

                if (recaptchaComp && recaptchaComp.current) {
                  recaptchaComp.current.reset();
                }
              }}
              disabled={isLogged ? !isChecked : !validated || !isChecked || (recaptchaValue === '' && !urlParams.bpr)}
            />
          </div>
        </ModalBody>
      </VbzModal>
    </>
  );
};
export default ModalConfirmation;
